import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { QuicklinkModule, QuicklinkStrategy } from 'ngx-quicklink';
import { AuthGuard } from './core/guards/auth/auth.guard';
import { SearchGuard } from './core/guards/search/search.guard';
import { UserProfileGuard } from './core/guards/user-profile/user-profile.guard';
import { PageNotFoundComponent } from './modules/auth/components/page-not-found/page-not-found.component';
import { TempUserGuard } from './core/guards/temp-user/temp-user.guard';
const routes: Routes = [
    {
        path: '',
        redirectTo: '/login',
        pathMatch: 'full',
    },
    {
        path: 'login',
        loadComponent: () =>
            import('@modules/auth/pages/login/login.page').then(
                m => m.LoginPage
            ),
    },
    {
        path: 'activation-warning',
        loadComponent: () =>
            import(
                '@modules/auth/pages/activation-warning/activation-warning.page'
            ).then(m => m.ActivationWarningPage),
    },
    {
        path: 'signup',
        loadComponent: () =>
            import('@modules/auth/pages/signup/signup.page').then(
                m => m.SignupPage
            ),
    },
    {
        path: 'complete',
        loadComponent: () =>
            import('@modules/auth/pages/complete/complete.page').then(
                m => m.CompletePage
            ),
    },
    {
        path: 'activate',
        loadComponent: () =>
            import('@modules/auth/pages/activate/activate.page').then(
                m => m.ActivatePage
            ),
    },
    {
        path: 'forgot',
        loadComponent: () =>
            import('@modules/auth/pages/forgot/forgot.page').then(
                m => m.ForgotPage
            ),
    },
    {
        path: 'reset-password',
        loadComponent: () =>
            import(
                '@modules/auth/pages/rest-password/rest-password.page'
            ).then(m => m.RestPasswordPage),
    },
    {
        path: 'sent-password',
        loadComponent: () =>
            import(
                '@modules/auth/pages/sent-password-change/sent-password-change.page'
            ).then(m => m.SentPasswordChangePage),
    },
    {
        path: 'search',
        loadChildren: () =>
            import(
                '@modules/program/program.routes'
            ).then(m => m.PROGRAM_SEARCH_ROUTES),
        canActivate: [SearchGuard],
    },
    {
        path: 'program',
        loadChildren: () =>
            import('@modules/program/program.routes').then(m => m.PROGRAM_ROUTES),
    },
    {
        path: 'dashboard',
        loadComponent: () =>
            import(
                './modules/user/pages/user-dashboard/user-dashboard.page'
            ).then(m => m.DashboardPage),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: 'profile',
        loadChildren: () =>
            import('@modules/user/profile.routes').then(
                m => m.PROFILE_ROUTES
            ),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: 'services',
        loadComponent: () =>
            import(
                '@modules/user/pages/user-services/user-services.page'
            ).then(m => m.UserServicesPage),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: 'contact',
        loadChildren: () =>
            import(
                '@modules/application/pages/application-help-chat/application-help-chat.routes'
            ).then(m => m.APPLICATION_HELP_CHAT_ROUTES),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: 'chat',
        loadChildren: () =>
            import(
                '@modules/application/pages/application-help-chat/application-help-chat.routes'
            ).then(m => m.APPLICATION_HELP_CHAT_ROUTES),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: 'payment',
        loadComponent: () =>
            import(
                '@modules/payment/pages/payment-details/payment-details.page'
            ).then(m => m.PaymentPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'twofactor',
        loadComponent: () =>
            import('@modules/auth/pages/two-factor/two-factor.page').then(
                m => m.TwoFactorPage
            ),
    },
    {
        path: 'application',
        loadChildren: () =>
            import('@modules/application/application.routes').then(
                m => m.APPLICATION_ROUTES
            ),
    },
    {
        path: 'documents',
        loadChildren: () =>
            import('@modules/document/documents.routes').then(
                m => m.DOCUMENTS_ROUTES
            ),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: '__auth',
        loadComponent: () =>
            import(
                '@modules/auth/pages/central-landing/central-landing.page'
            ).then(m => m.CentralLandingPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'my-profile-block',
        loadComponent: () =>
            import(
                '@modules/user/pages/user-profile-block/user-profile-block.page'
            ).then(m => m.UserProfileBlockPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'rest-password',
        loadComponent: () =>
            import(
                '@modules/auth/pages/rest-password/rest-password.page'
            ).then(m => m.RestPasswordPage),
    },
    {
        path: 'payment-success',
        loadComponent: () =>
            import(
                '@modules/payment/pages/payment-success/payment-success.page'
            ).then(m => m.PaymentSuccessPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'wire-transfer-details',
        loadComponent: () =>
            import(
                '@modules/payment/pages/wire-transfer-details/wire-transfer-details.page'
            ).then(m => m.WireTransferDetailsPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'payment-booking',
        loadComponent: () =>
            import(
                '@modules/payment/pages/payment-booking/payment-booking.page'
            ).then(m => m.PaymentBookingPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'favorites',
        loadComponent: () =>
            import(
                '@modules/favorite/pages/favorites-list/favorites-list.page'
            ).then(m => m.FavoritesListPage),
        canActivate: [AuthGuard],
    },
    {
        path: 'application-chain',
        loadChildren: () =>
            import('@modules/application-chain/application-chain.routes').then(
                m => m.APPLICATION_CHAIN_ROUTES
            ),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        path: 'itinerary-create',
        loadComponent: () =>
            import(
                '@modules/itinerary/pages/itinerary-create/itinerary-create.page'
            ).then(m => m.ItineraryCreatePage),
        canActivate: [AuthGuard, UserProfileGuard],
    },
    {
        // ** ALWAYS KEEP LAST OR ELSE YOURE GONNA HAVE A BAD TIME MMMM'KAY */
        path: '**',
        component: PageNotFoundComponent,
    },
];

@NgModule({
    imports: [
        QuicklinkModule,
        RouterModule.forRoot(routes, {
            preloadingStrategy: QuicklinkStrategy,
            // onSameUrlNavigation: 'reload',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
